import React, { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CopyableField from '~/components/Common/CopyableField';

import TENANTS from '~/consts/tenant';

import { useGeneratePasswordResetToken } from '~/hooks/apis/users/useGeneratePasswordResetToken';

import UsersService from '~/services/UsersService';
import { customerPortalHost } from '~/services/common';

interface Props {
  user: { id_member: string; email: string; tenant: string };
  onClose: () => void;
}

export default function ResetPasswordDialog({ user, onClose }: Props) {
  const [method, setMethod] = useState('link');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const tokenMutation = useGeneratePasswordResetToken();
  const isPending = isSaving || tokenMutation.isPending;
  const accountTenant = Object.values(TENANTS).find((candidate) => candidate.value === user.tenant);
  const portalHost = accountTenant ? customerPortalHost(accountTenant) : undefined;
  const token = tokenMutation.data;
  const resetLink =
    token && portalHost ? `${portalHost.replace(/\/$/, '')}/reset-password/${encodeURIComponent(token)}` : undefined;

  const close = () => {
    if (!isPending) onClose();
  };

  const savePassword = async () => {
    setPasswordError('');
    setIsSaving(true);
    try {
      await UsersService.resetPassword(user.id_member, user.email, newPassword);
      onClose();
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Unable to reset password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth aria-labelledby="reset-password-title">
      <DialogTitle id="reset-password-title">Reset password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">Account: {user.email}</Typography>
          <FormControl disabled={isPending}>
            <FormLabel id="password-reset-method-label">Reset method</FormLabel>
            <RadioGroup
              aria-labelledby="password-reset-method-label"
              value={method}
              onChange={(event) => {
                setMethod(event.target.value);
                setPasswordError('');
                setNewPassword('');
                tokenMutation.reset();
              }}
            >
              <FormControlLabel value="link" control={<Radio />} label="Generate reset link" />
              <FormControlLabel value="password" control={<Radio />} label="Set a password" />
            </RadioGroup>
          </FormControl>
          {method === 'link' ? (
            <>
              <Typography variant="body2">
                Generate a link to share with the account holder so they can choose their own password. No email is sent
                automatically.
              </Typography>
              {tokenMutation.isError && <Alert severity="error">{tokenMutation.error.message}</Alert>}
              {token && (
                <>
                  <Alert severity="success">Password reset token generated.</Alert>
                  {resetLink ? (
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">Reset link</Typography>
                      <CopyableField value={resetLink} tooltip="Copy reset link" sx={{ overflowWrap: 'anywhere' }} />
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      A reset link is unavailable for this brand. You can copy the token below.
                    </Alert>
                  )}
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">Reset token</Typography>
                    <CopyableField value={token} tooltip="Copy reset token" sx={{ overflowWrap: 'anywhere' }} />
                  </Stack>
                </>
              )}
            </>
          ) : (
            <>
              {passwordError && <Alert severity="error">{passwordError}</Alert>}
              <TextField
                fullWidth
                label="New password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isPending}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isPending}>
          {token ? 'Close' : 'Cancel'}
        </Button>
        {method === 'link' ? (
          <Button
            variant="contained"
            disabled={isPending || Boolean(token)}
            onClick={() => tokenMutation.mutate({ email: user.email, tenant: user.tenant })}
          >
            {tokenMutation.isPending ? 'Generating...' : 'Generate reset link'}
          </Button>
        ) : (
          <Button variant="contained" onClick={savePassword} disabled={!newPassword || isPending}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
